export interface FigureCursor {
  /** The column's key; "" for the record as a whole. */
  key: string;
  /** The part's index in the figure's parts; null on the figure itself. */
  part: number | null;
}

export const sameCursor = (a: FigureCursor, b: FigureCursor) =>
  a.key === b.key && a.part === b.part;

export const visibleItems = (
  keys: string[],
  partCount: (key: string) => number,
  expanded: ReadonlySet<string>,
): FigureCursor[] =>
  keys.flatMap((key) => [
    { key, part: null },
    ...(expanded.has(key)
      ? Array.from({ length: partCount(key) }, (_, part) => ({ key, part }))
      : []),
  ]);

export type TreeAction =
  | { type: "move"; to: FigureCursor }
  | { type: "toggle"; key: string; open: boolean }
  /** Enter on a figure opens or closes it; on a part, follows it. */
  | { type: "activate"; at: FigureCursor };

/**
 * The tree's keys, as a file tree's: up and down through the rows shown,
 * Home and End to the first and last, right opens a figure and then steps
 * into its parts, left steps back out and then closes it, Enter (or Space)
 * activates. Null for any other key.
 */
export const treeKey = ({
  key,
  items,
  active,
  partCount,
  expanded,
}: {
  key: string;
  items: FigureCursor[];
  active: FigureCursor;
  partCount: (key: string) => number;
  expanded: ReadonlySet<string>;
}): TreeAction | null => {
  const index = Math.max(
    items.findIndex((item) => sameCursor(item, active)),
    0,
  );
  const last = items.length - 1;
  const parts = partCount(active.key);
  const open = expanded.has(active.key);
  switch (key) {
    case "ArrowDown":
      return { type: "move", to: items[Math.min(index + 1, last)] };
    case "ArrowUp":
      return { type: "move", to: items[Math.max(index - 1, 0)] };
    case "Home":
      return { type: "move", to: items[0] };
    case "End":
      return { type: "move", to: items[last] };
    case "ArrowRight":
      if (active.part !== null || !parts) return null;
      return open
        ? { type: "move", to: { key: active.key, part: 0 } }
        : { type: "toggle", key: active.key, open: true };
    case "ArrowLeft":
      if (active.part !== null)
        return { type: "move", to: { key: active.key, part: null } };
      return open ? { type: "toggle", key: active.key, open: false } : null;
    case "Enter":
    case " ":
      return { type: "activate", at: active };
    default:
      return null;
  }
};
